import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Flex } from 'antd';
import { useFormik } from 'formik';

import {
  useAppDispatch,
  useAppSelector,
  useNavigationToListRequests,
  useRequisitionInfoExistingReq,
} from '@Hooks/index';
import { FooterActions } from '@Enum/index';
import { createdUserRequestSelector } from '@Store/selectors/userInfoSelector';
import { parseDateTime } from '@Utils/transformDateTime';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import { MainForm } from '@Components/index';
import {
  MaterialRequisitionDocumentResponse,
  MaterialRequisitionFormResponse,
  MaterialRequisitionItemFormResponse,
} from '@Types/api/form';
import { NotificationContext } from '@Context/NotificationContext';
import ApproveList from '@Components/MainForm/components/ApproveList/ApproveList';
import { ApproveListData } from '@Components/MainForm/components/ApproveList/types';
import { ReassignWindow } from '@Components/ReassignWindow';
import { EMPTY_FIELD_PLACEHOLDER } from '@Constants';
import {
  getCompaniesDictSelector,
  getCostCenterTypeDict,
  getCurrencies,
  getMrForDict,
  getWarrantyTypeDict,
} from '@Store/selectors/dictionarySelector';
import { useRequisitionAprroveMutation } from '@Store/api/approvalsListApiSlice';
import { RequisitionApproveRejectRequest } from '@Types/api/form/materialRequisition/Approve/RequisitionApproveRequest';

import {
  useGetCostCenterMutation,
  useGetEmpNumberByUserIdQuery,
} from '@Store/api/dictionaryApiSlice';

import { isNotNullValues } from '@Utils/isNotNullValues';
import { RequisitionStatuses } from '@Features/MaterialRequest/enum/RequisitionStatuses.enum';
import { RequestListSearchResponse } from '@Types/api/requestList';
import { initialValuesForm } from '../initialValues';
import {
  currentMaterialManagest,
  materialCostCenters,
  materialStoreOverhead,
} from '../../store/selectors/materialRequestSelector';
import { materialRequestActions } from '../../store/materialRequestSlice';
import { StepType } from '../StepType.enum';
import {
  useGetRequisitionCodeDescItemsMutation,
  useGetRequisitionCommodityClassQuery,
  useGetRequisitionDocumentsMutation,
  useGetRequisitionItemsMutation,
  useGetStoreOverheadMutation,
} from '../../store/api/MaterialRequestApiSlice';

import { isCostCenterMatchReceiveType } from '../utils/isCostCenterMatchReceiveType';
import {
  findCompanyType,
  findCostCenter,
  findCostCenterType,
  findMrForType,
} from '../../../utils/finder';
import MaterialFormChanger from '../components/MaterialFormChanger';
import MaterialDetails from './components/MaterialDetails/MaterialDetails';
import DocumentDetails from './components/DocumentDetails/DocumentDetails';
import {
  ITAdminFields,
  PlantEngineerFields,
  PlantWorkshopManagerFields,
  PurchaseOfficerFields,
} from './components/AdditionalFields';

interface MaterialRequisitionApproveFormProps {
  formData: MaterialRequisitionFormResponse;
  taskId: string;
}

const labels = ['Request Details', 'Material Details', 'Document Details'];
const defaultHeaderStepperItems = ['request header', 'materials', 'documents'];

const MaterialRequisitionApproveForm: React.FC<
  MaterialRequisitionApproveFormProps
> = ({ formData, taskId }) => {
  const dispatch = useAppDispatch();
  const {
    taskApprovedNotification,
    taskRejectedNotification,
    errorNotification,
  } = useContext(NotificationContext);
  const { handleBackToListRequests } = useNavigationToListRequests();
  const [requistionApproveRejectForm] = useRequisitionAprroveMutation();
  const [getRequisitionCodeDescItems] =
    useGetRequisitionCodeDescItemsMutation();
  const [getRequisitionItems, { isLoading }] = useGetRequisitionItemsMutation();
  const [getRequisitionDocuments] = useGetRequisitionDocumentsMutation();
  const [getStoreOverhead] = useGetStoreOverheadMutation();
  const companies = useAppSelector(getCompaniesDictSelector);
  const costCenterTypes = useAppSelector(getCostCenterTypeDict);
  const currentMaterialCostCenters = useAppSelector(materialCostCenters);
  const currentMaterialStoreOverhead = useAppSelector(materialStoreOverhead);
  const currencies = useAppSelector(getCurrencies);
  const warrantyTypes = useAppSelector(getWarrantyTypeDict);
  const mrForTypes = useAppSelector(getMrForDict);
  const [getCostCenter] = useGetCostCenterMutation();
  const [purchaseOfficerValues, setPurchaseOfficerValues] = useState({
    purchaseOfficer: '',
  });
  const [plantEngineerValues, setPlantEngineerValues] = useState({
    plantEngineer: '',
  });
  const [plantWorkshopManagerValues, setPlantWorkshopManagerValues] = useState({
    plantWorkshopManager: '',
  });
  const [itAdminValues, setITAdminValues] = useState({
    itAdmin: '',
  });
  const requestedUser = useAppSelector(createdUserRequestSelector);
  const { data: createdUser } = useGetEmpNumberByUserIdQuery(
    formData?.createdBy,
    {
      skip: !formData?.createdBy,
    }
  );
  const requisitionInfo = useRequisitionInfoExistingReq(
    {
      createdDate: formData?.createdOn || '',
      reqStatusId: formData?.statusId || 0,
      requestId: formData?.requisitionId || 0,
      requisitionNo: formData?.reqNo || '',
    },
    'Material'
  );
  const { step } = useAppSelector(currentMaterialManagest);
  const [comments, setCommentValue] = useState('');
  const [isReassignOpen, setReassignOpen] = useState(false);
  const [requisitionItems, setRequisitionItems] =
    useState<RequestListSearchResponse<MaterialRequisitionItemFormResponse>>();
  const [listItems, setListItems] =
    useState<MaterialRequisitionItemFormResponse[]>();
  const [listDocumentItems, setListDocumentItems] = useState<
    Array<MaterialRequisitionDocumentResponse>
  >([]);
  const { data: isShowITAdmin } = useGetRequisitionCommodityClassQuery(
    formData?.requisitionId,
    {
      skip:
        formData?.statusId !==
        RequisitionStatuses.AWAITING_APPROVAL_OF_WAREHOUSE_MANAGER,
    }
  );

  const formik = useFormik({
    initialValues: initialValuesForm,
    enableReinitialize: true,
    onSubmit: (): void => {},
  });

  const isPurchaseOfficerRequest = useMemo(() => {
    if (
      formData?.statusId ===
      RequisitionStatuses.AWAITING_APPROVAL_OF_PROCUREMENT_MANAGER
    ) {
      return true;
    }

    return false;
  }, [formData?.statusId]);

  const isPlantEngineerRequest = useMemo(() => {
    if (
      formData?.statusId ===
      RequisitionStatuses.AWAITING_APPROVAL_OF_SENIOR_STORE_KEEPER
    ) {
      return true;
    }

    return false;
  }, [formData?.statusId]);

  const isPlantWorkshopManagerRequest = useMemo(() => {
    if (
      formData?.statusId ===
      RequisitionStatuses.AWAITING_APPROVAL_OF_PLANT_ENGINEER
    ) {
      return true;
    }

    return false;
  }, [formData?.statusId]);

  const isITAdminRequest = useMemo(() => {
    if (
      formData?.statusId ===
        RequisitionStatuses.AWAITING_APPROVAL_OF_WAREHOUSE_MANAGER &&
      isShowITAdmin
    ) {
      return true;
    }

    return false;
  }, [formData?.statusId, isShowITAdmin]);

  const handleApproveReject = useCallback(
    (action: FooterActions.APPROVE | FooterActions.REJECT): void => {
      let body: RequisitionApproveRejectRequest = {
        taskId: String(taskId),
        decision: action,
        comment: comments,
        selectedApproverUserId: 0,
      };

      if (isPurchaseOfficerRequest) {
        const { purchaseOfficer } = purchaseOfficerValues;

        if (!purchaseOfficer && action === FooterActions.APPROVE) {
          errorNotification('Please Choose the Purchase Officer');
          return;
        }

        body = {
          ...body,
          selectedApproverUserId: Number(purchaseOfficer),
          purchaseOfficerId: Number(purchaseOfficer),
        };
      }

      if (isPlantEngineerRequest) {
        const { plantEngineer } = plantEngineerValues;

        if (!plantEngineer && action === FooterActions.APPROVE) {
          errorNotification('Please Choose the Plant Engineer');
          return;
        }

        body = {
          ...body,
          selectedApproverUserId: Number(plantEngineer),
        };
      }

      if (isPlantWorkshopManagerRequest) {
        const { plantWorkshopManager } = plantWorkshopManagerValues;

        if (!plantWorkshopManager && action === FooterActions.APPROVE) {
          errorNotification('Please Choose the Plant Workshop Manager');
          return;
        }

        body = {
          ...body,
          selectedApproverUserId: Number(plantWorkshopManager),
        };
      }

      if (isITAdminRequest) {
        const { itAdmin } = itAdminValues;
        body = {
          ...body,
          selectedApproverUserId: Number(itAdmin),
        };
      }

      if (action === FooterActions.REJECT && !comments) {
        errorNotification('Please leave a comment');
        return;
      }

      requistionApproveRejectForm(body)
        .unwrap()
        .then(() => {
          if (action === FooterActions.APPROVE) {
            taskApprovedNotification();
          } else {
            taskRejectedNotification();
          }

          handleBackToListRequests();
        });
    },
    [
      comments,
      taskId,
      purchaseOfficerValues,
      plantEngineerValues,
      plantWorkshopManagerValues,
      itAdminValues,
      isPurchaseOfficerRequest,
      isPlantEngineerRequest,
      isPlantWorkshopManagerRequest,
      isITAdminRequest,
      handleBackToListRequests,
    ]
  );

  useEffect(() => {
    if (
      step === StepType.REQUEST_HEADER &&
      isNotNullValues(formik.values.compId, formik.values.ccTypeId) &&
      createdUser
    ) {
      getCostCenter({
        allocationId: String(formik.values.ccTypeId),
        compId: String(formik.values.compId),
        empNo: createdUser,
      })
        .unwrap()
        .then((response) =>
          dispatch(materialRequestActions.setMaterialCostCenters(response))
        );
    }
  }, [
    step,
    formik.values.compId,
    formik.values.ccTypeId,
    isNotNullValues,
    createdUser,
  ]);

  useEffect(() => {
    if (
      step === StepType.REQUEST_HEADER &&
      isNotNullValues(
        formik.values.compId,
        formik.values.ccTypeId,
        formik.values.ccId
      )
    ) {
      if (
        isCostCenterMatchReceiveType(
          Number(formik.values.ccTypeId),
          Number(formik.values.ccId)
        )
      ) {
        getStoreOverhead({
          ccTypeId: formik.values.ccTypeId!,
          compId: formik.values.compId!,
          ccId: formik.values.ccId!,
        })
          .unwrap()
          .then(
            (response) =>
              response &&
              dispatch(
                materialRequestActions.setMaterialStoreOverhead(response)
              )
          );
      }
    }
  }, [
    step,
    formik.values.compId,
    formik.values.ccId,
    formik.values.ccTypeId,
    isNotNullValues,
    isCostCenterMatchReceiveType,
  ]);

  useEffect(() => {
    if (!formData) {
      return;
    }

    formik.setValues({
      ...formData,
    });
  }, [formData]);

  useEffect(() => {
    if (!formData) {
      return;
    }

    dispatch(
      materialRequestActions.setMaterialHeaderInfo({
        companyName:
          findCompanyType(formData.compId, companies)?.compName || '',
        compId: formData.compId,
        costCenterId: formData.ccId,
        costCenter:
          findCostCenter(currentMaterialCostCenters, formData.ccId)?.name || '',
        costCenterType:
          findCostCenterType(costCenterTypes, formData.ccTypeId)?.codeDesc ||
          '',
        mrFor: findMrForType(mrForTypes, formData.mrFor)?.codeDesc || '',
        unit: '',
      })
    );
  }, [formData, currentMaterialCostCenters]);

  useEffect(() => {
    if (
      step === StepType.MATERIALS &&
      formData?.requisitionId &&
      !requisitionItems?.content?.length
    ) {
      getRequisitionItems({
        requisitionId: formData?.requisitionId,
      })
        .unwrap()
        .then((response) => setRequisitionItems(response));
    }
  }, [step, formData?.requisitionId]);

  useEffect(() => {
    if (requisitionItems?.content?.length && !isLoading) {
      getRequisitionCodeDescItems({ reqId: Number(formData?.requisitionId) })
        .unwrap()
        .then((descriptionData) => {
          const mappedData = requisitionItems?.content?.map(
            (requisitionItem) => {
              const findDescriptionItem = descriptionData.find(
                (itemDesc) => itemDesc.reqItemId === requisitionItem.reqItemId
              );

              return findDescriptionItem
                ? {
                    ...requisitionItem,
                    actName: findDescriptionItem.data.actName,
                    resDesc: findDescriptionItem.data.resDesc,
                    matDesc: findDescriptionItem.data.matDesc,
                    plantCode: findDescriptionItem.data.plantCode,
                    plantName: findDescriptionItem.data.plantName,
                    ledgerName: findDescriptionItem.data.ledgerName,
                  }
                : requisitionItem;
            }
          );

          setListItems(mappedData);
        });
    }
  }, [requisitionItems, isLoading]);

  useEffect(() => {
    if (step === StepType.DOCUMENTS && !listDocumentItems.length) {
      getRequisitionDocuments(String(formData?.requisitionId))
        .unwrap()
        .then((response) => setListDocumentItems(response || []));
    }
  }, [step, formData?.requisitionId]);

  const dataSourceRequestDetails = useMemo(() => {
    let companyName = EMPTY_FIELD_PLACEHOLDER;
    let mrFor = EMPTY_FIELD_PLACEHOLDER;
    let costCenterType = EMPTY_FIELD_PLACEHOLDER;
    let costCenter = EMPTY_FIELD_PLACEHOLDER;
    let currencyType = EMPTY_FIELD_PLACEHOLDER;
    let warrantyType = EMPTY_FIELD_PLACEHOLDER;

    if (formik.values.compId) {
      companyName =
        findCompanyType(formik.values.compId, companies)?.compName ||
        EMPTY_FIELD_PLACEHOLDER;
    }

    if (formik.values.mrFor) {
      mrFor =
        findMrForType(mrForTypes, formik.values.mrFor)?.codeDesc ||
        EMPTY_FIELD_PLACEHOLDER;
    }

    if (formik.values.ccTypeId) {
      costCenterType =
        findCostCenterType(costCenterTypes, formik.values.ccTypeId)?.codeDesc ||
        EMPTY_FIELD_PLACEHOLDER;
    }

    if (formik.values.ccId) {
      costCenter =
        findCostCenter(currentMaterialCostCenters, formik.values.ccId)?.name ||
        EMPTY_FIELD_PLACEHOLDER;
    }

    if (formik.values.warrantyTypeId) {
      warrantyType =
        warrantyTypes.find(
          (warranty) => warranty.codeNum === formik.values.warrantyTypeId
        )?.codeDesc || EMPTY_FIELD_PLACEHOLDER;
    }

    currencyType =
      currencies.find((curr) => curr.currencyId === formik.values.currency)
        ?.currencyCode || EMPTY_FIELD_PLACEHOLDER;

    const output = [
      {
        label: 'Request Date',
        value: parseDateTime(formik.values.reqDate) || EMPTY_FIELD_PLACEHOLDER,
      },
      {
        label: 'Required On',
        value:
          parseDateTime(formik.values.requiredOn) || EMPTY_FIELD_PLACEHOLDER,
      },
      {
        label: 'Currency',
        value: currencyType || EMPTY_FIELD_PLACEHOLDER,
      },
      {
        label: 'Company',
        value: companyName || EMPTY_FIELD_PLACEHOLDER,
      },
      {
        label: 'MR For',
        value: mrFor || EMPTY_FIELD_PLACEHOLDER,
      },
      {
        label: 'Request Description',
        value: formik.values.headerDesc || EMPTY_FIELD_PLACEHOLDER,
      },
      {
        label: 'Remarks',
        value: formik.values.remarks || EMPTY_FIELD_PLACEHOLDER,
      },
      {
        label: 'Cost Center Type',
        value: costCenterType || EMPTY_FIELD_PLACEHOLDER,
      },

      {
        label: 'Cost Center',
        value: costCenter || EMPTY_FIELD_PLACEHOLDER,
      },
      currentMaterialStoreOverhead?.storeName && {
        label: 'Additional Value',
        value:
          currentMaterialStoreOverhead.storeName || EMPTY_FIELD_PLACEHOLDER,
      },
      {
        label: 'Warranty Type',
        value: warrantyType || EMPTY_FIELD_PLACEHOLDER,
      },
      formik.values.warrantyPeriod && {
        label: 'Warranty Period',
        value: formik.values.warrantyPeriod || EMPTY_FIELD_PLACEHOLDER,
      },
      formik.values.warrantyEndDate && {
        label: 'Warranty End Date',
        value:
          parseDateTime(formik.values.warrantyEndDate) ||
          EMPTY_FIELD_PLACEHOLDER,
      },
      {
        label: 'Emergency Purchase',
        value: formik.values.emergencyPurchase ? 'Yes' : 'No',
      },
    ];

    return output.filter((item) => Boolean(item)) as ApproveListData;
  }, [
    formik.values,
    findCompanyType,
    findCostCenterType,
    findCostCenter,
    findMrForType,
    companies,
    mrForTypes,
    warrantyTypes,
    currentMaterialCostCenters,
    currentMaterialStoreOverhead,
  ]);

  const handleFooterAction = useCallback(
    (action: FooterActions, payload?: string) => {
      if (action === FooterActions.APPROVE || action === FooterActions.REJECT) {
        handleApproveReject(action);
      }

      if (action === FooterActions.COMMENTS_CHANGE) {
        setCommentValue(payload || '');
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }

      if (action === FooterActions.REASSIGN) {
        setReassignOpen(true);
      }
    },
    [handleBackToListRequests, handleApproveReject]
  );

  const handleSwitchStep = useCallback(
    (stepNum: number): void => {
      dispatch(materialRequestActions.setSwitchStep(stepNum));
    },
    [dispatch]
  );

  const headerStepperConfig = {
    items: defaultHeaderStepperItems,
    counter: step,
    onClick: handleSwitchStep,
    allowAll: true,
  };

  const approveBtnName = useMemo(() => {
    if (
      formData?.statusId === RequisitionStatuses.UNDER_PROCESS_WITH_PURCHASE
    ) {
      return 'Complete';
    }

    return undefined;
  }, [formData?.statusId]);

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        handleSubmit={formik.handleSubmit}
        userInfo={requestedUser}
        requisitionInfo={requisitionInfo}
        formType="approval"
        showMRsection
        onFooterAction={handleFooterAction}
        headerStepperOptions={headerStepperConfig}
        requestHeaderStep={false}
        approveBtnName={approveBtnName}
      >
        <FormItems>
          <MaterialFormChanger label={labels[step]} step={step}>
            <ApproveList
              dataSource={dataSourceRequestDetails}
              label="Request Details"
            />
            <MaterialDetails list={listItems || []} />
            <DocumentDetails
              requisitionId={formData?.requisitionId}
              list={listDocumentItems || []}
            />
          </MaterialFormChanger>
        </FormItems>
        {isPurchaseOfficerRequest && (
          <PurchaseOfficerFields setValues={setPurchaseOfficerValues} />
        )}
        {isPlantEngineerRequest && (
          <PlantEngineerFields setValues={setPlantEngineerValues} />
        )}
        {isPlantWorkshopManagerRequest && (
          <PlantWorkshopManagerFields
            setValues={setPlantWorkshopManagerValues}
          />
        )}
        {isITAdminRequest && (
          <ITAdminFields
            compId={formData?.compId}
            setValues={setITAdminValues}
          />
        )}
      </MainForm>
      <ReassignWindow
        creatorRequestNumber={requestedUser?.empNumber}
        taskId={taskId}
        isOpen={isReassignOpen}
        onClose={() => {
          setReassignOpen(false);
        }}
        onUserSelected={() => {
          setReassignOpen(false);
          handleBackToListRequests();
        }}
      />
    </Flex>
  );
};

export default MaterialRequisitionApproveForm;
